import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@/api/business';
import { createNonResearchReason } from '@/api/business/nonResearchReason';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';

interface AddDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const formSchema = z.object({
  DS_MOTIVO_NAO_PESQUISA: z
    .string()
    .min(1, 'A descrição do motivo é obrigatória')
    .max(255, 'Máximo de 255 caracteres'),
});

type FormSchema = z.infer<typeof formSchema>;

const AddDialog = ({ isOpen, onClose }: AddDialogProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      DS_MOTIVO_NAO_PESQUISA: '',
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createNonResearchReason,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['non-research-reason'] });
      toast({
        title: 'Sucesso',
        description: 'Motivo de não pesquisa cadastrado com sucesso!',
      });
      form.reset();
      onClose();
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        toast({
          title: 'Erro ao cadastrar',
          description:
            err.response?.data?.message ||
            'Não foi possível cadastrar o motivo.',
          variant: 'destructive',
        });
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const onSubmit = (values: FormSchema) => {
    mutate({
      DS_MOTIVO_NAO_PESQUISA: values.DS_MOTIVO_NAO_PESQUISA,
      ATIVO: 1,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Novo Motivo de Não Pesquisa</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="DS_MOTIVO_NAO_PESQUISA"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição do Motivo</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Estabelecimento fechado"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button variant="outline" type="button" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddDialog;
